import AdminNav from '@/components/admin-nav';
import { admin_tag_path } from '@/rails/routes';
import type { AdminTagsIndex, WorkspacePageProps } from '@/types';
import { tagTokenColor } from '@/pages/reports/components/tag-picker';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Badge } from '@astryxdesign/core/Badge';
import { Card } from '@astryxdesign/core/Card';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { Text } from '@astryxdesign/core/Text';
import { Token } from '@astryxdesign/core/Token';
import { VStack } from '@astryxdesign/core/VStack';
import { router, usePage } from '@inertiajs/react';
import { Tags } from 'lucide-react';
import { useState } from 'react';

type Props = WorkspacePageProps & AdminTagsIndex;

interface TagToDelete {
  id: string;
  label: string;
}

const Index = () => {
  const { tags } = usePage<Props>().props;
  const [tagToDelete, setTagToDelete] = useState<TagToDelete | null>(null);

  const confirmDeleteTag = () => {
    if (tagToDelete) {
      router.delete(admin_tag_path(tagToDelete.id), { preserveScroll: true });
      setTagToDelete(null);
    }
  };

  return (
    <VStack gap={6} width="100%" className="iorio-tags-page">
      <AdminNav />

      <Card padding={6} className="iorio-tags-card">
        <VStack gap={5}>
          <HStack hAlign="between" vAlign="start" gap={3} wrap="wrap">
            <VStack gap={1}>
              <HStack gap={2} vAlign="center">
                <span className="iorio-tags-icon" aria-hidden="true">
                  <Icon icon={Tags} size="sm" />
                </span>
                <Heading level={2}>Etiquetas del equipo</Heading>
              </HStack>
              <Text type="supporting" color="secondary">
                Eliminarlas no modifica las versiones ya guardadas.
              </Text>
            </VStack>
            <Badge label={`${tags.length} totales`} variant="red" />
          </HStack>

          {tags.length === 0 ? (
            <section className="iorio-tags-empty" aria-label="Estado vacío de etiquetas">
              <EmptyState
                title="Todavía no hay etiquetas"
                description="Cuando alguien cree una etiqueta desde el parte diario, va a aparecer acá."
                icon={
                  <span className="iorio-tags-icon" aria-hidden="true">
                    <Icon icon={Tags} size="sm" />
                  </span>
                }
                isCompact
              />
            </section>
          ) : (
            <ul className="iorio-tags-list" aria-label="Etiquetas disponibles">
              {tags.map((tag) => (
                <li key={tag.id} className="iorio-tags-item">
                  <Token
                    label={tag.label}
                    color={tagTokenColor(tag)}
                    icon={<Tags size={14} />}
                    onRemove={() => setTagToDelete({ id: tag.id, label: tag.label })}
                  />
                </li>
              ))}
            </ul>
          )}
        </VStack>
      </Card>

      <AlertDialog
        isOpen={tagToDelete != null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setTagToDelete(null);
        }}
        title={tagToDelete ? `¿Eliminar la etiqueta “${tagToDelete.label}”?` : 'Eliminar etiqueta'}
        description="Se quita del catálogo compartido. Las versiones ya guardadas no cambian."
        cancelLabel="Cancelar"
        actionLabel="Eliminar etiqueta"
        onAction={confirmDeleteTag}
      />
    </VStack>
  );
};

export default Index;
